import { useCallback } from 'react';
import amplitudeLog from '@client/amplitude';
import { useSetCalculationPeriod } from '@features/scope/calculation-period.hooks';
import {
  selectScopePreset,
  selectSavedScopePreset,
} from '@features/scope/scope.store';
import { AMP_PROJECT_VIEW_HEADER_SCOPE_PRESET_SELECT } from '@shared/constants';
import { setSavedGroupStates } from './lifecycle-scope.hooks';
import { ScopePreset } from './scope-presets.data';

/** Make a preset the current scope. The calculation period is applied separately. */
export const applyScopePreset = (
  preset: ScopePreset,
  organization?: string,
): void => {
  if (organization) selectSavedScopePreset(preset, organization);
  else selectScopePreset(preset.id);
  // Drop stale per-group snapshots so a later group toggle cannot restore pre-preset phases.
  setSavedGroupStates({});
  amplitudeLog(AMP_PROJECT_VIEW_HEADER_SCOPE_PRESET_SELECT, {
    preset: preset.id,
  });
};

/**
 * Apply a scope preset to the persisted scope and, when the preset defines a
 * calculation period, to the project's `building_lifetime`. Writing the preset's
 * own period cannot detach the scope it was just attached to.
 */
export const useApplyScopePreset = (): ((
  preset: ScopePreset,
  organization?: string,
) => void) => {
  const setCalculationPeriod = useSetCalculationPeriod();
  return useCallback(
    (preset: ScopePreset, organization?: string) => {
      applyScopePreset(preset, organization);
      if (preset.calculationPeriodYears === undefined) return;
      setCalculationPeriod(preset.calculationPeriodYears);
    },
    [setCalculationPeriod],
  );
};
